import { PaginationMeta } from "../lib/bufferedPagination";
import {
  AddressFormData,
  CountryDistributionStat,
  DirectorFormData,
  KybComplianceDetail,
  KybDirector,
  KybRequirement,
  KybComplianceRow,
  KybComplianceStatus,
  KybAssignmentStatus,
  KybStatsSummary,
  KybStatusBadgeTone,
  KybStatusCountMap,
  KybStatusStat,
} from "../../features/kyb-compliance/types";
import { apiClient } from "./client";
import { KYB_ENDPOINTS } from "../../features/kyb-compliance/services/endpoints";

type AnyObject = Record<string, unknown>;

export type GetKybBusinessesParams = {
  routePath?: string;
  search?: string;
  sort?: string;
  country?: string;
  status?: string;
  type?: string;
  businessType?: string;
  page?: number;
  limit?: number;
};

export type GetKybStatsParams = {
  sort?: "all" | "month" | "year" | "day";
  businessType?: string;
};

export type KybBusinessesResult = {
  items: KybComplianceRow[];
  meta?: PaginationMeta;
};

export type KybBusinessDetailResult = {
  item: KybComplianceDetail | null;
};

export type VerifyKybRequirementPayload = {
  businessId: string;
  requirementKey: string;
  status: "APPROVED" | "REJECTED" | "QUERIED";
  comment?: string;
  targetId?: string;
  type?: "director" | "ubo";
};

export type UpdateKybRequirementValuePayload = {
  businessId: string;
  requirementKey: string;
  textValue?: string;
  director?: DirectorFormData;
  address?: AddressFormData;
  targetId?: string;
};

export type UpdateKybRequirementDocumentPayload = {
  businessId: string;
  requirementKey: string;
  document: {
    url: string;
    key: string;
  };
};

export type AddKybAdminDocumentPayload = {
  businessId: string;
  document: {
    url: string;
    key: string;
  };
};

export type UploadKybDocumentResult = {
  url: string;
  key: string;
};

export type UpdateKybBusinessStatusPayload = {
  businessId: string;
  status: "APPROVED" | "REJECTED" | "QUERIED";
  reason?: string;
};

export type EscalateKybVerificationPayload = {
  businessId: string;
  link: string;
  adminEmail?: string;
  businessName: string;
  adminFirstName?: string;
  role?: string;
  roleName?: string;
  note?: string;
};

export type ExportKybBusinessesParams = {
  search?: string;
  sort?: string;
  country?: string;
  status?: KybComplianceStatus;
  businessType?: string;
};

type NormalizeKybBusinessOptions = {
  defaultStatus?: KybComplianceStatus;
};

const STATUS_COLORS: Record<KybComplianceStatus, string> = {
  started: "#FFC44D",
  "under-review": "#B7A0FF",
  approved: "#ADF0A5",
  rejected: "#FF9698",
  "not-started": "#E4DDFF",
};

const STATUS_LABELS: Record<KybComplianceStatus, string> = {
  started: "Started",
  "under-review": "Under review",
  approved: "Approved",
  rejected: "Rejected",
  "not-started": "Not started",
};

const COUNTRY_FALLBACK_COLORS = ["#7C3AED", "#B79BFF", "#E4DDFF", "#9F6BFF"];

const asObject = (value: unknown): AnyObject | null => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as AnyObject;
  }
  return null;
};

const asString = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const asNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, "").trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const asBoolean = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(normalized)) return true;
    if (["false", "no", "0"].includes(normalized)) return false;
  }
  return undefined;
};

const unwrapDisplayValue = (value: unknown): unknown => {
  const record = asObject(value);
  if (!record) return value;

  return (
    record.value ??
    record.name ??
    record.textValue ??
    record.label ??
    record.currency ??
    value
  );
};

const formatDisplayDate = (value: unknown) => {
  const raw = asString(value).trim();
  if (!raw) return "-";

  const parsedDate = new Date(raw);
  if (Number.isNaN(parsedDate.getTime())) return raw;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
};

const titleCase = (value: string) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const toCountryRequestValue = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "uk") return "UK";
  if (normalized === "us") return "US";
  if (normalized === "nigeria") return "Nigeria";
  return titleCase(value);
};

const formatCountry = (value: unknown) => {
  const countryRecord = asObject(value);
  const raw = asString(
    countryRecord?.name ?? countryRecord?.code ?? value,
  ).trim();
  if (!raw) return "-";

  const normalized = raw.toUpperCase();
  if (normalized === "NG") return "Nigeria";
  if (normalized === "UK" || normalized === "GB") return "UK";
  if (normalized === "US" || normalized === "USA") return "US";

  return raw.length <= 3 ? normalized : titleCase(raw);
};

const normalizeStatus = (value: unknown): KybComplianceStatus => {
  const normalized = asString(value)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

  if (normalized.includes("reject")) return "rejected";
  if (normalized.includes("approve")) return "approved";
  if (normalized.includes("submit")) return "under-review";
  if (normalized === "started" || normalized === "start") return "started";
  if (normalized.includes("not-start")) return "not-started";
  if (normalized.includes("review")) return "under-review";

  return "under-review";
};

const normalizeAssignmentStatus = (
  value: unknown,
): KybAssignmentStatus | undefined => {
  const normalized = asString(value)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

  if (!normalized) return undefined;
  if (normalized.includes("active")) return "active";
  if (normalized.includes("unassigned")) return "unassigned";
  if (normalized.includes("need")) return "needs-assignment";
  if (normalized.includes("assign")) return "needs-assignment";

  return undefined;
};

const toDisplayLabel = (value: string) =>
  value
    .split(/[_\s-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const formatStatusLabel = (status: KybComplianceStatus) =>
  status
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeKycStatusDisplay = (
  value: unknown,
): { label: string | null; tone: KybStatusBadgeTone } => {
  if (value === null || value === undefined) {
    return { label: null, tone: "neutral" };
  }

  const raw = asString(value).trim();
  if (!raw) {
    return { label: null, tone: "neutral" };
  }

  const normalized = raw.toLowerCase().replace(/[_\s]+/g, "-");

  if (normalized.includes("active") || normalized.includes("approve")) {
    return { label: toDisplayLabel(raw), tone: "success" };
  }

  if (normalized.includes("unassign") || normalized.includes("reject")) {
    return { label: toDisplayLabel(raw), tone: "danger" };
  }

  if (normalized.includes("need") || normalized.includes("assign")) {
    return { label: toDisplayLabel(raw), tone: "warning" };
  }

  return { label: toDisplayLabel(raw), tone: "neutral" };
};

const toStatusRequestValue = (status: KybComplianceStatus) => {
  switch (status) {
    case "under-review":
      return "under review";
    case "not-started":
      return "not started";
    default:
      return status;
  }
};

const toExportStatusRequestValue = (status: KybComplianceStatus) => {
  switch (status) {
    case "under-review":
      return "under-review";
    case "not-started":
      return "not-started";
    default:
      return status;
  }
};

const getDocumentMetrics = (record: AnyObject) => {
  const documents = asObject(record.documents);
  const documentStats = asObject(record.documentStats);
  const kycStats = asObject(record.kycStats);
  const metrics = asObject(record.metrics);

  return {
    pending:
      asNumber(record.pendingDocuments) ||
      asNumber(record.pending_documents) ||
      asNumber(kycStats?.pending) ||
      asNumber(documents?.pending) ||
      asNumber(documentStats?.pending) ||
      asNumber(metrics?.pendingDocuments),
    total:
      asNumber(record.totalDocuments) ||
      asNumber(record.total_documents) ||
      asNumber(kycStats?.total) ||
      asNumber(documents?.total) ||
      asNumber(documentStats?.total) ||
      asNumber(metrics?.totalDocuments),
    approved:
      asNumber(record.approvedDocuments) ||
      asNumber(record.approved_documents) ||
      asNumber(kycStats?.approved) ||
      asNumber(documents?.approved) ||
      asNumber(documentStats?.approved) ||
      asNumber(metrics?.approvedDocuments),
    rejected:
      asNumber(record.rejectedDocuments) ||
      asNumber(record.rejected_documents) ||
      asNumber(kycStats?.rejected) ||
      asNumber(documents?.rejected) ||
      asNumber(documentStats?.rejected) ||
      asNumber(metrics?.rejectedDocuments),
  };
};

const normalizeKybBusiness = (
  item: unknown,
  index: number,
  options: NormalizeKybBusinessOptions = {},
): KybComplianceRow => {
  const record = asObject(item) ?? {};
  const customer = asObject(record.customer) ?? asObject(record.user);
  const business = asObject(record.business);
  const documentMetrics = getDocumentMetrics(record);

  const customerName =
    asString(record.customerName).trim() ||
    asString(record.customer_name).trim() ||
    [asString(record.firstName).trim(), asString(record.lastName).trim()]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    [asString(customer?.firstName).trim(), asString(customer?.lastName).trim()]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    asString(customer?.name).trim() ||
    asString(customer?.fullName).trim() ||
    asString(record.contactName).trim() ||
    "Unknown Customer";

  const customerEmail =
    asString(record.customerEmail).trim() ||
    asString(record.customer_email).trim() ||
    asString(record.email).trim() ||
    asString(customer?.email).trim() ||
    "-";

  const businessName =
    asString(record.businessName).trim() ||
    asString(record.business_name).trim() ||
    asString(unwrapDisplayValue(record.name)).trim() ||
    asString(record.legalName).trim() ||
    asString(unwrapDisplayValue(business?.name)).trim() ||
    "-";

  const businessType =
    asString(record.businessType).trim() ||
    asString(record.business_type).trim() ||
    asString(record.type).trim() ||
    asString(record.entityType).trim() ||
    asString(business?.type).trim() ||
    "-";

  const phoneNumber =
    asString(record.phoneNumber).trim() ||
    asString(record.phone_number).trim() ||
    asString(customer?.phoneNumber).trim() ||
    asString(customer?.phone_number).trim() ||
    asString(business?.phoneNumber).trim() ||
    asString(business?.phone_number).trim() ||
    "-";

  const country =
    formatCountry(record.country) !== "-"
      ? formatCountry(record.country)
      : formatCountry(record.country_name) !== "-"
        ? formatCountry(record.country_name)
        : formatCountry(business?.country);

  const statusSource =
    record.status ??
    record.kybStatus ??
    record.kyb_status ??
    record.reviewStatus;
  const status = asString(statusSource).trim()
    ? normalizeStatus(statusSource)
    : (options.defaultStatus ?? "under-review");
  const kycStatusDisplay = normalizeKycStatusDisplay(
    record.kycStatus ?? record.kyc_status,
  );
  const assignmentStatus = normalizeAssignmentStatus(
    record.kycAssignmentStatus ??
      record.kyc_assignment_status ??
      record.assignment ??
      record.assignmentStatus ??
      record.assignment_status ??
      record.assigneeStatus ??
      record.assignee_status,
  );
  const displayBusinessType = businessType || "-";
  const displayBusinessName = businessName || "-";

  return {
    id:
      asString(record._id).trim() ||
      asString(record.id).trim() ||
      asString(record.businessId).trim() ||
      asString(record.business_id).trim() ||
      asString(business?._id).trim() ||
      asString(business?.id).trim() ||
      asString(business?.businessId).trim() ||
      `kyb-business-${index}`,
    customerName,
    customerEmail,
    businessName: displayBusinessName,
    businessType: displayBusinessType,
    totalDocuments: documentMetrics.total,
    pendingDocuments: documentMetrics.pending,
    approvedDocuments: documentMetrics.approved,
    rejectedDocuments: documentMetrics.rejected,
    country,
    phoneNumber,
    registrationDate: formatDisplayDate(
      record.registrationDate ??
        record.registeredAt ??
        record.createdAt ??
        record.created_at,
    ),
    status,
    isQueried: asBoolean(record.isQueried ?? record.is_queried),
    queryStatus: pickFirstNonEmpty(
      record.queryStatus,
      record.query_status,
      (record as any).queryStatus,
      (record as any).query_status,
    ) || undefined,
    kycAssignmentStatus: assignmentStatus,
    kycStatusLabel: kycStatusDisplay.label ?? undefined,
    kycStatusTone: kycStatusDisplay.label ? kycStatusDisplay.tone : undefined,
  };
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      const record = asObject(item);
      return asString(
        record?.flag ??
          record?.currency ??
          record?.code ??
          record?.name ??
          item,
      ).trim();
    })
    .filter(Boolean);
};

const getAddressFromObject = (value: unknown) => {
  const record = asObject(value);
  if (!record) return "";

  const parts = [
    asString(record.houseNumber).trim(),
    asString(record.line1).trim(),
    asString(record.line2).trim(),
    asString(record.street).trim(),
    asString(record.city).trim(),
    asString(record.state).trim(),
    asString(record.country).trim(),
    asString(record.zip).trim(),
    asString(record.zipcode).trim(),
    asString(record.postalCode).trim(),
  ].filter(Boolean);

  return parts.join(", ");
};

const pickFirstNonEmpty = (...values: unknown[]) =>
  values
    .map((value) => asString(unwrapDisplayValue(value)).trim())
    .find(Boolean) ?? "";

const getNatureOfBusiness = (value: unknown) => {
  const record = asObject(value);
  if (!record) return asString(value).trim();

  return (
    asString(record.nature).trim() ||
    asString(record.value).trim() ||
    asString(record.name).trim() ||
    asString(record.code).trim()
  );
};

const getTransactionVolume = (value: unknown) => {
  const record = asObject(value);
  if (!record) return asString(value).trim();

  const currency = asString(record.currency).trim();
  const amount = asString(record.amount).trim();
  const volume = asString(record.volume).trim();

  return [currency, amount || volume].filter(Boolean).join(" ");
};

const getSourceOfFunds = (value: unknown) => {
  const record = asObject(value);
  if (!record) return asString(value).trim();

  return [asString(record.type).trim(), asString(record.name).trim()]
    .filter(Boolean)
    .join(" • ");
};

const formatObjectValue = (value: unknown) => {
  const record = asObject(value);
  if (!record) return "";

  return [
    asString(record.street).trim(),
    asString(record.city).trim(),
    asString(record.state).trim(),
    asString(record.country).trim(),
    asString(record.zipcode).trim(),
    asString(record.zip).trim(),
    asString(record.postalCode).trim(),
  ]
    .filter(Boolean)
    .join(", ");
};

const humanizeRequirementKey = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "rc_number" || normalized === "rc-number") {
    return "RC Number";
  }

  return value
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const normalizeDirectors = (value: unknown): KybDirector[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<KybDirector[]>((accumulator, item) => {
    const record = asObject(item);
    if (!record) return accumulator;

    const country = asObject(record.country);

    accumulator.push({
      firstName: asString(record.firstName).trim(),
      lastName: asString(record.lastName).trim(),
      email: asString(record.email).trim() || undefined,
      targetId:
        asString(record.targetId).trim() ||
        asString(record._id).trim() ||
        asString(record.id).trim() ||
        undefined,
      phoneNumber: asString(record.phoneNumber).trim() || undefined,
      phoneVerified: asBoolean(record.phoneStatus),
      title: asString(record.title).trim() || undefined,
      gender: asString(record.gender).trim() || undefined,
      dateOfBirth: formatDisplayDate(record.dateOfBirth),
      percentageOwnership:
        asString(record.percentageOwnership).trim() || undefined,
      natureOfControl:
        asString(record.natureOfControl).trim() ||
        asString(record.controlType).trim() ||
        undefined,
      bvnNumber:
        asString(record.bvnNumber).trim() || asString(record.bvn).trim(),
      idNumber: asString(record.idNumber).trim() || undefined,
      nationality:
        formatCountry(record.nationality) !== "-"
          ? formatCountry(record.nationality)
          : formatCountry(country?.name ?? country?.code),
      residentialAddress: getAddressFromObject(record.address) || undefined,
      identificationType: humanizeRequirementKey(
        asString(record.meanOfIdentificationType).trim(),
      ),
      identificationUrl:
        asString(record.meanOfIdentificationUrl).trim() || undefined,
      status: asString(record.status).trim() || undefined,
      comment: asString(record.comment).trim() || undefined,
    });

    return accumulator;
  }, []);
};

const normalizeKycRequirements = (value: unknown): KybRequirement[] => {
  if (!Array.isArray(value)) return [];

  return value.reduce<KybRequirement[]>((accumulator, item) => {
    const record = asObject(item);
    if (!record) return accumulator;
    const document = asObject(record.document);
    const directors = normalizeDirectors(record.directors);
    const ubos = normalizeDirectors(record.ubos);
    const peopleValue = [...directors, ...ubos]
      .map((person, index) => {
        const name = `${person.firstName} ${person.lastName}`.trim();
        const value = [name, person.bvnNumber].filter(Boolean).join(" • ");

        return value || `Person ${index + 1}`;
      })
      .filter(Boolean)
      .join(", ");
    const textValue =
      asString(record.textValue).trim() ||
      formatObjectValue(record.objectValue) ||
      peopleValue ||
      undefined;

    const rawKey = asString(record.key).trim();
    const rawLabel = asString(record.label).trim();
    const displayLabel = rawLabel || humanizeRequirementKey(rawKey);

    accumulator.push({
      id:
        asString(record._id).trim() ||
        asString(record.id).trim() ||
        rawKey,
      key: displayLabel,
      label: displayLabel,
      rawKey: rawKey || undefined,
      type:
        asString(record.type).trim().toLowerCase() === "document"
          ? "document"
          : "field",
      status: asString(record.status).trim() || "PENDING",
      textValue,
      objectValue: asObject(record.objectValue) ?? undefined,
      directors: directors.length ? directors : undefined,
      ubos: ubos.length ? ubos : undefined,
      comment: asString(record.comment).trim() || undefined,
      reviewedAt: asString(record.reviewedAt).trim() || undefined,
      documentUrl: asString(document?.url).trim() || undefined,
      documentKey: asString(document?.key).trim() || undefined,
    });

    return accumulator;
  }, []);
};

const firstObjectFromArray = (value: unknown) => {
  if (!Array.isArray(value)) return null;
  return value.map((item) => asObject(item)).find(Boolean) ?? null;
};

const unwrapKybBusinessRecord = (payload: unknown): AnyObject | null => {
  const root = asObject(payload);
  if (!root) return firstObjectFromArray(payload);

  const data = asObject(root.data);
  const dataData = data ? asObject(data.data) : null;
  const candidates: unknown[] = [
    firstObjectFromArray(root.data),
    firstObjectFromArray(data?.data),
    root.business,
    root.item,
    root.result,
    root.record,
    root.kyb,
    data?.business,
    data?.item,
    data?.result,
    data?.record,
    data?.kyb,
    dataData,
    data,
    root,
  ];

  return candidates.map((item) => asObject(item)).find(Boolean) ?? null;
};

const normalizeKybBusinessDetail = (
  payload: unknown,
  fallbackRow?: KybComplianceRow | null,
  requestedBusinessId?: string,
): KybComplianceDetail | null => {
  const record = unwrapKybBusinessRecord(payload);
  if (!record && !fallbackRow) return null;
  const hasWebsite = (payload as any)?.hasWebsite

  const baseRow = normalizeKybBusiness(record ?? {}, 0, {
    defaultStatus: fallbackRow?.status,
  });
  const resolvedBusinessId =
    baseRow.id.startsWith("kyb-business-")
      ? requestedBusinessId || fallbackRow?.id || baseRow.id
      : baseRow.id;
  const user = asObject(record?.user);
  const business = asObject(record?.business);
  const proofOfAddress = asObject(record?.proofOfAddress);
  const businessAddress = asObject(record?.businessAddress);
  const directors = normalizeDirectors(record?.directors);
  const ubos = normalizeDirectors(record?.UBOs ?? record?.ubos);
  const kycRequirements = normalizeKycRequirements(record?.kycRequirements);
  const requirementDirectors = kycRequirements.flatMap(
    (requirement) => requirement.directors ?? [],
  );
  const requirementUbos = kycRequirements.flatMap(
    (requirement) => requirement.ubos ?? [],
  );
  const additionalInformation = Array.isArray(record?.additionalInformation)
    ? (record?.additionalInformation as Record<string, any>[])
    : undefined;
  const wallets = asStringArray(
    record?.wallets ??
      record?.currencies ??
      record?.walletCurrencies ??
      business?.wallets ??
      business?.currencies,
  );
  const highRiskActivities = asStringArray(record?.activities);
  const registeredContact = pickFirstNonEmpty(
    record?.registeredContact,
    record?.contactName,
    record?.ownerName,
    [asString(user?.firstName).trim(), asString(user?.lastName).trim()]
      .filter(Boolean)
      .join(" "),
    fallbackRow?.customerName,
    baseRow.customerName,
  );
  const registeredAddress = pickFirstNonEmpty(
    record?.registeredAddress,
    record?.registered_address,
    getAddressFromObject(record?.registeredAddress),
    getAddressFromObject(record?.registered_address),
    getAddressFromObject(record?.address),
    getAddressFromObject(businessAddress),
    getAddressFromObject(business?.registeredAddress),
    getAddressFromObject(business?.address),
  );
  const physicalAddress = pickFirstNonEmpty(
    record?.physicalAddress,
    record?.physical_address,
    getAddressFromObject(record?.physicalAddress),
    getAddressFromObject(record?.physical_address),
    getAddressFromObject(business?.physicalAddress),
    getAddressFromObject(businessAddress),
    registeredAddress,
  );
  const endpointStatusLabel = pickFirstNonEmpty(
    record?.status,
    record?.kybStatus,
    record?.kyb_status,
    record?.reviewStatus,
  );

  return {
    ...baseRow,
    id: resolvedBusinessId,
    customerName: fallbackRow?.customerName || baseRow.customerName,
    customerEmail: fallbackRow?.customerEmail || baseRow.customerEmail,
    businessName: baseRow.businessName || fallbackRow?.businessName || "-",
    businessType: baseRow.businessType || fallbackRow?.businessType || "-",
    totalDocuments: baseRow.totalDocuments || fallbackRow?.totalDocuments,
    pendingDocuments:
      baseRow.pendingDocuments || fallbackRow?.pendingDocuments || 0,
    approvedDocuments:
      baseRow.approvedDocuments || fallbackRow?.approvedDocuments || 0,
    rejectedDocuments:
      baseRow.rejectedDocuments || fallbackRow?.rejectedDocuments || 0,
    country:
      (baseRow.country !== "-" ? baseRow.country : "") ||
      fallbackRow?.country ||
      "-",
    registrationDate:
      (baseRow.registrationDate !== "-" ? baseRow.registrationDate : "") ||
      fallbackRow?.registrationDate ||
      "-",
    status: baseRow.status || fallbackRow?.status || "under-review",
    statusLabel: endpointStatusLabel
      ? toDisplayLabel(endpointStatusLabel)
      : formatStatusLabel(
          baseRow.status || fallbackRow?.status || "under-review",
        ),
    registeredContact: registeredContact || "-",
    hasWebsite,
    businessWebsite:
      pickFirstNonEmpty(
        record?.businessWebsite,
        record?.website,
        business?.businessWebsite,
        business?.website,
      ) || "-",
    phoneNumber:
      pickFirstNonEmpty(
        record?.phoneNumber,
        record?.phone_number,
        user?.phoneNumber,
        user?.phone_number,
        business?.phoneNumber,
      ) || "-",
    rcNumber:
      pickFirstNonEmpty(
        record?.registrationNumber,
        record?.registration_number,
        record?.rcNumber,
        record?.rc_number,
        business?.rcNumber,
        business?.rc_number,
      ) || "-",
    tin: pickFirstNonEmpty(record?.tin, record?.TIN, business?.tin),
    tier: pickFirstNonEmpty(record?.tier, business?.tier),
    stage: pickFirstNonEmpty(record?.stage, business?.stage),
    canTransfer:
      typeof record?.canTransfer === "boolean" ? record.canTransfer : undefined,
    canOnRampWithPartna:
      typeof record?.canOnRampWithPartna === "boolean"
        ? record.canOnRampWithPartna
        : undefined,
    financeLimit: pickFirstNonEmpty(record?.financeLimit, business?.financeLimit),
    isAllowAPIAccess:
      typeof record?.isAllowAPIAccess === "boolean"
        ? record.isAllowAPIAccess
        : undefined,
    businessSubmissionDate:
      formatDisplayDate(record?.businessSubmittionDate) !== "-"
        ? formatDisplayDate(record?.businessSubmittionDate)
        : formatDisplayDate(record?.businessSubmissionDate),
    businessStatusUpdateDate: formatDisplayDate(
      record?.businessStatusUpdateDate,
    ),
    lastUpdatedDate: formatDisplayDate(record?.updatedAt ?? record?.updated_at),
    webPresence:
      pickFirstNonEmpty(
        record?.businessWebsite,
        record?.business_website,
        record?.webPresence,
        record?.web_presence,
        record?.website,
        record?.domain,
        business?.website,
        business?.domain,
      ) ||
      (hasWebsite === false ? "No website" : "-"),
    registeredAddress: registeredAddress || "-",
    physicalAddress: physicalAddress || "-",
    natureOfBusiness:
      pickFirstNonEmpty(
        getNatureOfBusiness(record?.natureOfBusiness),
        record?.nature_of_business,
        getNatureOfBusiness(business?.natureOfBusiness),
        business?.description,
      ) || "-",
    isHighRisk: asBoolean(record?.isHighRisk),
    highRiskActivities,
    wallets,
    phoneVerified: Boolean(record?.phoneStatus),
    proofOfAddressType: pickFirstNonEmpty(proofOfAddress?.type),
    proofOfAddressStatus: pickFirstNonEmpty(proofOfAddress?.status),
    proofOfAddressReason: pickFirstNonEmpty(proofOfAddress?.reason),
    transactionVolume:
      pickFirstNonEmpty(
        getTransactionVolume(record?.monthlyTransactionVolume),
        record?.transactionVolume,
        record?.transaction_volume,
        business?.transactionVolume,
      ) || "-",
    sourceOfFunds:
      pickFirstNonEmpty(
        getSourceOfFunds(record?.sourceOfFund),
        record?.sourceOfFunds,
        record?.source_of_funds,
        business?.sourceOfFunds,
      ) || "-",
    informationRequestStatus: pickFirstNonEmpty(
      record?.informationRequestStatus,
      record?.information_request_status,
      record?.infoRequestStatus,
    ),
    queryStatus: pickFirstNonEmpty(
      record?.queryStatus,
      record?.query_status,
      (payload as any)?.queryStatus,
      (payload as any)?.query_status,
      business?.queryStatus,
      business?.query_status,
    ) || undefined,
    directors: directors.length ? directors : requirementDirectors,
    ubos: ubos.length ? ubos : requirementUbos,
    kycRequirements,
    additionalInformation,
  };
};

const getMeta = (payload: unknown): PaginationMeta | undefined => {
  const root = asObject(payload);
  if (!root) return undefined;

  const nestedData = asObject(root.data);
  const meta =
    asObject(root.meta) ?? (nestedData ? asObject(nestedData.meta) : null);

  if (!meta) return undefined;

  return {
    currentPage:
      typeof meta.currentPage === "number" ? meta.currentPage : undefined,
    totalPages:
      typeof meta.totalPages === "number" ? meta.totalPages : undefined,
    hasNext: typeof meta.hasNext === "boolean" ? meta.hasNext : undefined,
    hasPrevious:
      typeof meta.hasPrevious === "boolean" ? meta.hasPrevious : undefined,
    total: typeof meta.total === "number" ? meta.total : undefined,
    totalCount:
      typeof meta.totalCount === "number" ? meta.totalCount : undefined,
    count: typeof meta.count === "number" ? meta.count : undefined,
    limit: typeof meta.limit === "number" ? meta.limit : undefined,
    itemsPerPage:
      typeof meta.itemsPerPage === "number" ? meta.itemsPerPage : undefined,
  };
};

const normalizeKybBusinesses = (
  payload: unknown,
  options: NormalizeKybBusinessOptions = {},
): KybBusinessesResult => {
  if (Array.isArray(payload)) {
    return {
      items: payload.map((item, index) =>
        normalizeKybBusiness(item, index, options),
      ),
    };
  }

  const root = asObject(payload);
  if (!root) return { items: [] };

  const nestedData = asObject(root.data);
  const candidates = [
    root.data,
    root.businesses,
    root.items,
    root.results,
    nestedData?.businesses,
    nestedData?.items,
    nestedData?.results,
    nestedData?.data,
  ];

  const list = candidates.find((candidate) => Array.isArray(candidate));
  if (!Array.isArray(list)) return { items: [], meta: getMeta(payload) };

  return {
    items: list.map((item, index) =>
      normalizeKybBusiness(item, index, options),
    ),
    meta: getMeta(payload),
  };
};

const readStatusCount = (source: AnyObject | null, keys: string[]) => {
  if (!source) return 0;

  for (const key of keys) {
    const direct = source[key];
    if (direct !== undefined) return asNumber(direct);
  }

  return 0;
};

const normalizeStatusCounts = (payload: unknown): KybStatusCountMap => {
  const root = asObject(payload);
  const data = asObject(root?.data);
  const counts =
    asObject(root?.statusCounts) ??
    asObject(data?.statusCounts) ??
    asObject(root?.counts) ??
    asObject(data?.counts) ??
    asObject(root?.stats) ??
    asObject(data?.stats) ??
    asObject(root?.summary) ??
    asObject(data?.summary);

  return {
    "under-review": readStatusCount(counts, [
      "underReview",
      "under_review",
      "under review",
      "under-review",
      "inReview",
    ]),
    started: readStatusCount(counts, ["started", "start"]),
    "not-started": readStatusCount(counts, [
      "notStarted",
      "not_started",
      "not started",
      "not-started",
    ]),
    approved: readStatusCount(counts, ["approved"]),
    rejected: readStatusCount(counts, ["rejected"]),
  };
};

const normalizeStatusStats = (
  counts: KybStatusCountMap,
  payload: unknown,
): KybStatusStat[] => {
  const root = asObject(payload);
  const data = asObject(root?.data);
  const list =
    (Array.isArray(root?.statusDistribution) && root?.statusDistribution) ||
    (Array.isArray(data?.statusDistribution) && data?.statusDistribution) ||
    (Array.isArray(root?.statusStats) && root?.statusStats) ||
    (Array.isArray(data?.statusStats) && data?.statusStats);

  if (Array.isArray(list)) {
    return list
      .map((item) => {
        const record = asObject(item);
        if (!record) return null;
        const status = normalizeStatus(
          record.status ?? record.label ?? record.name ?? record.key,
        );
        return {
          label: STATUS_LABELS[status],
          color: STATUS_COLORS[status],
          value:
            asNumber(record.value) ||
            asNumber(record.count) ||
            counts[status] ||
            0,
        };
      })
      .filter((item): item is KybStatusStat => item !== null);
  }

  return (Object.keys(STATUS_LABELS) as KybComplianceStatus[]).map(
    (status) => ({
      label: STATUS_LABELS[status],
      color: STATUS_COLORS[status],
      value: counts[status] ?? 0,
    }),
  );
};

const normalizeCountryStats = (payload: unknown): CountryDistributionStat[] => {
  const root = asObject(payload);
  const data = asObject(root?.data);
  const listCandidate =
    (Array.isArray(root?.countryDistribution) && root?.countryDistribution) ||
    (Array.isArray(data?.countryDistribution) && data?.countryDistribution) ||
    (Array.isArray(root?.countries) && root?.countries) ||
    (Array.isArray(data?.countries) && data?.countries);

  if (Array.isArray(listCandidate)) {
    return listCandidate
      .map((item, index) => {
        const record = asObject(item);
        if (!record) return null;
        const label = formatCountry(
          record.label ??
            record.country ??
            record.name ??
            record.code ??
            record._id,
        );
        return {
          label: label === "-" ? `Country ${index + 1}` : label,
          value: asNumber(record.value) || asNumber(record.count),
          color:
            asString(record.color).trim() ||
            COUNTRY_FALLBACK_COLORS[index % COUNTRY_FALLBACK_COLORS.length],
        };
      })
      .filter((item): item is CountryDistributionStat => item !== null);
  }

  const objectCandidate =
    asObject(root?.countryDistribution) ?? asObject(data?.countryDistribution);

  if (objectCandidate) {
    return Object.entries(objectCandidate).map(([label, value], index) => ({
      label: formatCountry(label),
      value: asNumber(value),
      color: COUNTRY_FALLBACK_COLORS[index % COUNTRY_FALLBACK_COLORS.length],
    }));
  }

  return [];
};

const formatPercentageDelta = (value: unknown) => {
  const raw = asString(value).trim();
  if (raw) {
    if (raw.includes("%")) return raw;
    if (/^-?\d+(\.\d+)?$/.test(raw)) {
      const numeric = Number(raw);
      return `${numeric > 0 ? "+" : ""}${numeric}%`;
    }
    return raw;
  }

  const numeric = asNumber(value);
  if (!numeric) return "0%";
  return `${numeric > 0 ? "+" : ""}${numeric}%`;
};

const formatDuration = (
  value: unknown,
  unit: "minute" | "hour",
  fallback: string,
) => {
  const raw = asString(value).trim();
  if (raw) return raw;

  const numeric = asNumber(value);
  if (!numeric) return fallback;

  const suffix = numeric === 1 ? unit : `${unit}s`;
  return `${numeric} ${suffix}`;
};

const normalizeKybStats = (payload: unknown): KybStatsSummary => {
  const root = asObject(payload);
  const data = asObject(root?.data);
  const summary = asObject(root?.summary) ?? asObject(data?.summary);
  const growth = asObject(root?.growth) ?? asObject(data?.growth);
  const counts = normalizeStatusCounts(payload);

  return {
    totalRegistrations:
      asNumber(summary?.totalRegistrations) ||
      asNumber(summary?.total_registrations) ||
      asNumber(summary?.total) ||
      asNumber(root?.totalRegistrations) ||
      asNumber(data?.totalRegistrations) ||
      asNumber(root?.total) ||
      asNumber(data?.total),
    registrationsGrowth:
      formatPercentageDelta(
        summary?.registrationsGrowth ??
          summary?.growth ??
          growth?.total ??
          root?.registrationsGrowth ??
          data?.registrationsGrowth,
      ) || "0%",
    registrationsGrowthLabel:
      asString(summary?.growthLabel).trim() ||
      asString(root?.growthLabel).trim() ||
      "since last month",
    averageApprovalTime: formatDuration(
      summary?.averageApprovalTime ??
        summary?.average_approval_time ??
        root?.avgApprovalTime ??
        data?.avgApprovalTime ??
        root?.averageApprovalTime ??
        data?.averageApprovalTime,
      "minute",
      "0 minutes",
    ),
    averageProcessingTime:
      asString(summary?.averageProcessingTime).trim() ||
      asString(summary?.average_processing_time).trim() ||
      asString(root?.averageProcessingTime).trim() ||
      asString(data?.averageProcessingTime).trim() ||
      formatDuration(
        summary?.averageProcessingHours,
        "hour",
        "0 hours avg. in processing",
      ),
    statusCounts: counts,
    statusStats: normalizeStatusStats(counts, payload),
    countryDistribution: normalizeCountryStats(payload),
  };
};

export const getKybBusinesses = async (params: GetKybBusinessesParams) => {
  const { routePath = "/v1/admin/kyb/", ...requestParams } = params;
  const response = await apiClient.get(routePath, {
    params: requestParams,
  });

  return normalizeKybBusinesses(response.data, {
    defaultStatus:
      routePath === "/v1/admin/kyb/not-started" ? "not-started" : undefined,
  });
};

const getFilenameFromDisposition = (disposition?: string) => {
  if (!disposition) return null;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1];

  return null;
};

const buildKybExportParams = (params: ExportKybBusinessesParams) => {
  const requestParams: Record<string, string> = {};

  if (params.search?.trim()) {
    requestParams.search = params.search.trim();
  }

  if (params.sort) {
    requestParams.sort = params.sort;
  }

  if (params.country && params.country !== "all") {
    requestParams.country = toCountryRequestValue(params.country);
  }

  if (params.status && params.status !== "not-started") {
    requestParams.status = toExportStatusRequestValue(params.status);
  }

  if (params.businessType && params.businessType !== "all") {
    if (params.businessType === "llc") requestParams.type = "LLC";
    if (params.businessType === "sole-proprietorship") {
      requestParams.type = "soleproprietorship";
    }
    if (params.businessType === "partnership") {
      requestParams.type = "Partnership";
    }
  }

  return requestParams;
};

export const exportKybBusinesses = async (params: ExportKybBusinessesParams) => {
  const routePath =
    params.status === "not-started"
      ? "/v1/admin/kyb/not-started/export"
      : "/v1/admin/kyb/export";
  const response = await apiClient.get(routePath, {
    params: buildKybExportParams(params),
    responseType: "blob",
  });

  return {
    blob: response.data as Blob,
    filename:
      getFilenameFromDisposition(response.headers["content-disposition"]) ??
      "kyb-businesses.csv",
  };
};

export const getKybBusinessDetail = async (
  businessId: string,
  fallbackRow?: KybComplianceRow | null,
) => {
  const response = await apiClient.get(`/v1/admin/kyb/${businessId}`);

  return {
    item: normalizeKybBusinessDetail(response.data, fallbackRow, businessId),
  };
};

export const getKybStats = async (params: GetKybStatsParams) => {
  const response = await apiClient.get("/v1/admin/kyb/stats", {
    params,
  });

  return normalizeKybStats(response.data);
};

export const verifyKybRequirement = async ({
  businessId,
  requirementKey,
  status,
  comment,
  targetId,
  type,
}: VerifyKybRequirementPayload) => {
  const response = await apiClient.put(
    `/v1/admin/${businessId}/${encodeURIComponent(requirementKey)}`,
    {
      status,
      comment: comment?.trim() || undefined,
      targetId: targetId?.trim() || undefined,
      type,
    },
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const updateKybRequirementValue = async ({
  businessId,
  requirementKey,
  textValue,
  director,
  address,
  targetId,
}: UpdateKybRequirementValuePayload) => {
  const body: Record<string, unknown> = { key: requirementKey };

  if (director !== undefined) {
    body.director = { ...director, targetId: targetId ?? director.targetId };
  } else if (address !== undefined) {
    body.address = address;
  } else {
    body.textValue = textValue;
  }

  const response = await apiClient.patch(
    `/v1/admin/${businessId}/kyb/value`,
    body,
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const uploadKybDocumentFile = async (
  file: File,
): Promise<UploadKybDocumentResult> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post("/v1/utility/upload-file", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  const payload = response.data;
  const root = asObject(payload);
  const data = asObject(root?.data);
  const url =
    asString(root?.url).trim() ||
    asString(root?.link).trim() ||
    asString(data?.url).trim() ||
    asString(data?.link).trim() ||
    (typeof payload === "string" ? payload : "");
  const key =
    asString(root?.key).trim() ||
    asString(root?.name).trim() ||
    asString(data?.key).trim() ||
    asString(data?.name).trim() ||
    file.name;

  return {
    url,
    key,
  };
};

export const updateKybRequirementDocument = async ({
  businessId,
  requirementKey,
  document,
}: UpdateKybRequirementDocumentPayload) => {
  const response = await apiClient.patch(
    `/v1/admin/${businessId}/kyb/value`,
    {
      key: requirementKey,
      document,
    },
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const addKybAdminDocument = async ({
  businessId,
  document,
}: AddKybAdminDocumentPayload) => {
  const response = await apiClient.post(
    `/v1/admin/${businessId}/admin-document`,
    {
      url: document.url,
      key: document.key,
    },
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const updateKybBusinessStatus = async ({
  businessId,
  status,
  reason,
}: UpdateKybBusinessStatusPayload) => {
  const response = await apiClient.put(
    `/v1/admin/changeBusinessStatus/${businessId}`,
    {
      status,
      reason: reason?.trim() || undefined,
    },
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const escalateKybVerification = async ({
  businessId,
  link,
  adminEmail,
  businessName,
  adminFirstName,
  role,
  roleName,
  note,
}: EscalateKybVerificationPayload) => {
  const response = await apiClient.post(
    `/v1/admin/escalate-kyc/${businessId}`,
    {
      link,
      adminEmail,
      businessName,
      adminFirstName,
      role,
      roleName,
      note: note?.trim() || undefined,
    },
    {
      headers: {
        "x-business-id": businessId,
      },
    },
  );

  return response.data;
};

export const buildKybBusinessesParams = (params: {
  search?: string;
  sort?: string;
  country?: string;
  status?: KybComplianceStatus;
  businessType?: string;
  businessSource?: string;
  page?: number;
  limit?: number;
}): GetKybBusinessesParams => {
  const requestParams: GetKybBusinessesParams = {
    routePath:
      params.status === "not-started"
        ? "/v1/admin/kyb/not-started"
        : "/v1/admin/kyb/",
    sort: params.sort,
    page: params.page,
    limit: params.limit,
  };

  if (params.search?.trim()) {
    requestParams.search = params.search.trim();
  }

  if (params.businessSource && params.businessSource !== "all") {
    requestParams.businessType = params.businessSource;
  }

  if (
    params.status !== "not-started" &&
    params.country &&
    params.country !== "all"
  ) {
    requestParams.country = toCountryRequestValue(params.country);
  }

  if (params.status && params.status !== "not-started") {
    requestParams.status = toStatusRequestValue(params.status);
  }

  if (
    params.status !== "not-started" &&
    params.businessType &&
    params.businessType !== "all"
  ) {
    if (params.businessType === "llc") requestParams.type = "LLC";
    if (params.businessType === "sole-proprietorship") {
      requestParams.type = "soleproprietorship";
    }
    if (params.businessType === "partnership")
      requestParams.type = "Partnership";
  }

  return requestParams;
};

export interface IkybAddProps {
  businessId: string; data: {
    dueDate?: string;
    items: ({
      label?: string;
      key?: string;
      type?: string;
      subtype?: string;
      targetEmail?: string;
      targetId?: string;
    })[];
  };
}

export const kybAdditionalDataApi = async ({
  businessId, data }: IkybAddProps) => {
  try {
    const res = await apiClient.post(KYB_ENDPOINTS.addKybInformation({ businessId }), data)
    return res
  } catch (error) {
    throw error
  }
}

export const kybAdditionalUpdateDataApi = async ({
  businessId, data }: IkybAddProps) => {
  try {
    const res = await apiClient.post(KYB_ENDPOINTS.addKybInformationUpdate({ businessId }), data)
    return res
  } catch (error) {
    return error
  }
}

export const getBusinessCatalogue = async (businessId: string) => {
  try {
    const res = await apiClient.get(KYB_ENDPOINTS.getBusinessCatalogue(businessId), {
      headers: {
        "x-business-id": businessId,
      }
    })
    return res
  } catch (error) {
    return error
  }
}

export const approveSubmittedKyb = async (businessId: string) => {
  try {
    const res = await apiClient.patch(KYB_ENDPOINTS.approveSubmittedKyb({ businessId }))
    return res
  } catch (error) {
    return error
  }
}
